import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ChevronRight, Send, Shield, Truck, Clock, Award, ArrowRight } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const ADV_ICONS = [Shield, Truck, Clock, Award, Shield, Truck, Clock, Award];

function Counter({ end, suffix = "" }) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    useEffect(() => {
        if (!isInView) return;
        let start = 0;
        const step = end / 120;
        const timer = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(start));
        }, 16);
        return () => clearInterval(timer);
    }, [isInView, end]);
    return <span ref={ref}>{count}{suffix}</span>;
}

export default function HomePage() {
    const settings = useSettings();
    const [cars, setCars] = useState([]);
    useEffect(() => {
        api.get("/cars?status=in_stock").then((r) => setCars(r.data.slice(0, 4))).catch(() => {});
    }, []);

    const s = settings || {};
    const stats = s.stats || [];
    const advantages = (s.advantages || []).map((a, i) => ({ ...a, icon: ADV_ICONS[i % ADV_ICONS.length] }));
    const bgImg = s.background_image ? resolveImg(s.background_image) : null;

    return (
        <>
        <title>AutoTreider | Автомобили из Европы</title>
        <meta name="description" content={s.hero_subtitle || ""} />
        <link rel="canonical" href="https://autotreider.eu/" />
        <meta property="og:title" content="AutoTreider | Автомобили из Европы" />
        <meta property="og:url" content="https://autotreider.eu/" />
        <meta property="og:type" content="website" />
        <div data-testid="home-page" className="relative">
            <section className="relative h-screen flex items-center" data-testid="hero-section">
                <div className="absolute inset-0">
                    {bgImg && <img src={bgImg} alt="" className="w-full h-full object-cover" loading="eager" />}
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full">
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
                        <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-3 sm:mb-4 font-medium font-body">{s.company_name || ""}</p>
                        <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white leading-[1.1] mb-4 sm:mb-6 font-display">{s.hero_title || ""}</h1>
                        <p className="text-sm sm:text-base md:text-lg text-zinc-400 mb-6 sm:mb-10 max-w-lg leading-relaxed font-body">{s.hero_subtitle || ""}</p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <Link to="/catalog" className="bg-[#0099FF] text-white px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider font-medium hover:bg-[#0088ee] transition-all flex items-center justify-center gap-2 font-body" data-testid="hero-catalog-btn">
                                Смотреть каталог <ChevronRight className="w-4 h-4" />
                            </Link>
                            {s.telegram_channel_url && (
                                <a href={s.telegram_channel_url} target="_blank" rel="noopener noreferrer" className="border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-wider hover:bg-white/5 transition-all flex items-center justify-center gap-2 font-body" data-testid="hero-telegram-btn">
                                    <Send className="w-4 h-4" /> Telegram
                                </a>
                            )}
                        </div>
                    </motion.div>
                </div>
                {stats.length > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-md border-t border-white/5" data-testid="stats-bar">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-6 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
                            {stats.map((st, i) => (
                                <div key={i} className="text-center"><p className="text-xl sm:text-2xl md:text-3xl font-bold text-white font-display"><Counter end={st.value} suffix={st.suffix} /></p><p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider mt-1 font-body">{st.label}</p></div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {advantages.length > 0 && (
                <section className="py-16 sm:py-32 relative" data-testid="advantages-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                        <div className="text-center mb-8 sm:mb-16">
                            <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-2 sm:mb-3 font-body">{s.advantages_section_subtitle || ""}</p>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-8 sm:mb-16 font-display">{s.advantages_section_title || ""}</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                            {advantages.map((a, i) => (
                                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#09090b] border border-white/5 p-6 sm:p-8 hover:border-[#0099FF]/30 transition-all group">
                                    <a.icon className="w-8 sm:w-10 h-8 sm:h-10 text-[#0099FF] mb-4 sm:mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                                    <h3 className="text-white font-bold text-base sm:text-lg mb-2 sm:mb-3 font-display">{a.title}</h3>
                                    <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed font-body">{a.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {cars.length > 0 && (
                <section className="py-12 sm:py-24 bg-[#09090b]" data-testid="featured-cars">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 sm:mb-16 gap-4">
                            <div>
                                <p className="text-[#0099FF] text-xs uppercase tracking-[0.3em] mb-2 sm:mb-3 font-body">{s.cars_section_subtitle || ""}</p>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white font-display">{s.cars_section_title || ""}</h2>
                            </div>
                            <Link to="/catalog" className="text-[#0099FF] text-xs sm:text-sm flex items-center gap-2 hover:gap-3 transition-all uppercase tracking-wider font-body" data-testid="view-all-cars">Все автомобили <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                            {cars.map((car) => (
                                <Link to="/catalog" key={car.id} className="group cursor-pointer" data-testid={`car-card-${car.id}`}>
                                    <div className="aspect-[4/3] overflow-hidden mb-4 bg-zinc-900">
                                        <img src={resolveImg(car.images?.[0]) || ""} alt={`${car.brand} ${car.model}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>
                                    <h3 className="text-white font-bold font-display">{car.brand} {car.model}</h3>
                                    <p className="text-zinc-500 text-xs sm:text-sm mt-1 font-body">{car.year} / {car.mileage?.toLocaleString()} км</p>
                                    <p className="text-[#0099FF] font-bold mt-2 font-display">{car.price?.toLocaleString()} &#8381;</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {s.telegram_channel_url && (
                <section className="py-16 sm:py-32 relative" data-testid="cta-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
                        <div className="max-w-2xl mx-auto">
                            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 font-display">{s.cta_title || ""}</h2>
                            <p className="text-zinc-400 max-w-lg mx-auto mb-10 font-body">{s.cta_subtitle || ""}</p>
                            <a href={s.telegram_channel_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#0099FF] text-white px-10 py-4 text-sm uppercase tracking-wider font-medium hover:bg-[#0088ee] transition-all font-body" data-testid="cta-telegram-btn">
                                <Send className="w-5 h-5" /> {s.telegram_channel_button_text || "Telegram"}
                            </a>
                        </div>
                    </div>
                </section>
            )}
        </div>
        </>
    );
}
